import { useAuth, useTranslation } from '@/context/AuthContext';
import { Link, useLocation } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { Globe, Menu, X, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';

export const MarketingLayout = ({ children }) => {
  const { user, language, switchLanguage } = useAuth();
  const { t } = useTranslation();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  // Check if we're on landing page for transparent header
  const isLanding = location.pathname === '/';

  useEffect(() => {
    // Initial check
    setScrolled(window.scrollY > 50);
    
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { to: '/', label: t('nav.home') },
    { to: '/features', label: t('nav.features') },
    { to: '/pricing', label: t('nav.pricing') },
    { to: '/contact', label: t('nav.contact') },
  ];

  // Dynamic header styling based on scroll and page
  const headerBg = isLanding && !scrolled 
    ? '' 
    : 'bg-white/95 backdrop-blur-md shadow-sm';
  
  const textColor = isLanding && !scrolled 
    ? 'text-white' 
    : 'text-purple-700';

  const linkActiveClass = isLanding && !scrolled
    ? 'text-white bg-white/20'
    : 'text-purple-600 bg-purple-50';

  const linkHoverClass = isLanding && !scrolled
    ? 'hover:text-white hover:bg-white/15'
    : 'hover:text-purple-900 hover:bg-purple-50';

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header 
        className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${headerBg}`} 
        data-testid="marketing-header"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-20 flex items-center justify-between">
          {/* Logo - Use white on dark backgrounds, color on light */}
          <Link to="/" className="flex items-center gap-3" data-testid="logo-link">
            <img 
              src={isLanding && !scrolled ? "/images/logo_LP_white.png" : "/images/logo_LP_color.png"} 
              alt="LifePulse" 
              className="h-8 w-auto"
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-1">
            {navLinks.map(link => (
              <Link
                key={link.to}
                to={link.to}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                  location.pathname === link.to
                    ? linkActiveClass
                    : `${textColor} ${linkHoverClass}`
                }`}
                data-testid={`nav-${link.to.replace('/', '') || 'home'}`}
              >
                {link.label}
              </Link>
            ))}
          </nav>

          {/* Right Section */}
          <div className="hidden md:flex items-center gap-4">
            {/* Language Switcher */}
            <button
              onClick={() => switchLanguage(language === 'fr' ? 'en' : 'fr')}
              className={`flex items-center gap-1.5 px-3 py-2 rounded-full text-sm font-medium transition-all ${
                isLanding && !scrolled 
                  ? 'text-white/90 hover:bg-white/15 border border-white/30' 
                  : 'text-purple-600 hover:bg-purple-50 border border-purple-200'
              }`}
              data-testid="language-switcher"
            >
              <Globe className="w-4 h-4" />
              {language.toUpperCase()}
            </button>

            {user ? (
              <Link to="/dashboard">
                <Button 
                  className="rounded-full bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white px-6 py-5 text-sm font-bold shadow-lg shadow-purple-500/30" 
                  data-testid="go-dashboard-btn"
                >
                  {t('nav.dashboard')} <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            ) : (
              <>
                <Link to="/login">
                  <Button 
                    variant="ghost" 
                    className={`text-sm font-medium px-4 py-2 rounded-full ${
                      isLanding && !scrolled ? 'text-white hover:bg-white/15' : 'text-purple-600 hover:bg-purple-50'
                    }`} 
                    data-testid="login-btn"
                  >
                    {t('nav.login')}
                  </Button>
                </Link>
                <Link to="/login">
                  <Button 
                    className="rounded-full bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white px-6 py-5 text-sm font-bold shadow-lg shadow-purple-500/30 hover:scale-105 transition-transform" 
                    data-testid="get-started-btn"
                  >
                    {t('nav.getStarted')}
                  </Button>
                </Link>
              </>
            )}
          </div>

          {/* Mobile Menu Toggle */}
          <button 
            className={`md:hidden p-2 rounded-lg ${isLanding && !scrolled ? 'text-white' : 'text-purple-700'}`} 
            onClick={() => setMenuOpen(!menuOpen)} 
            data-testid="mobile-menu-toggle"
          >
            {menuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {menuOpen && (
          <div className="md:hidden bg-white border-t border-purple-100 shadow-xl animate-fade-in">
            <div className="px-4 py-6 space-y-2">
              {navLinks.map(link => (
                <Link 
                  key={link.to} 
                  to={link.to} 
                  onClick={() => setMenuOpen(false)}
                  className={`block px-4 py-3 rounded-xl text-base font-medium transition-colors ${
                    location.pathname === link.to
                      ? 'text-purple-600 bg-purple-50'
                      : 'text-purple-700 hover:bg-purple-50'
                  }`}
                >
                  {link.label}
                </Link>
              ))}
              <div className="pt-4 border-t border-purple-100 mt-4 space-y-3">
                <Link to="/login" onClick={() => setMenuOpen(false)}>
                  <Button className="w-full rounded-xl bg-gradient-to-r from-purple-500 to-pink-500 text-white py-6 text-base font-bold">
                    {user ? t('nav.dashboard') : t('nav.getStarted')}
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        )}
      </header>

      {/* Main Content - No padding-top for landing page */}
      <main className={isLanding ? '' : 'pt-20'}>{children}</main>

      {/* Footer */}
      <footer className="gradient-airhelp text-white py-16" data-testid="marketing-footer">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-10 mb-12">
            {/* Brand */}
            <div>
              <div className="flex items-center gap-3 mb-4">
                <img 
                  src="/images/logo_LP_white.png" 
                  alt="LifePulse" 
                  className="h-8 w-auto"
                />
              </div>
              <p className="text-purple-300 text-sm leading-relaxed">
                {language === 'fr' 
                  ? 'LifePulse vous aide à découvrir et vivre de vrais moments.' 
                  : 'LifePulse helps you discover and live real moments.'}
              </p>
            </div>
            
            {/* Product */}
            <div>
              <h4 className="text-white font-bold mb-4">{language === 'fr' ? 'Produit' : 'Product'}</h4>
              <div className="space-y-3">
                <Link to="/features" className="block text-purple-300 hover:text-white transition-colors">{t('nav.features')}</Link>
                <Link to="/pricing" className="block text-purple-300 hover:text-white transition-colors">{t('nav.pricing')}</Link>
              </div>
            </div>
            
            {/* Legal */}
            <div>
              <h4 className="text-white font-bold mb-4">{language === 'fr' ? 'Légal' : 'Legal'}</h4>
              <div className="space-y-3">
                <Link to="/security" className="block text-purple-300 hover:text-white transition-colors">
                  {language === 'fr' ? 'Confidentialité' : 'Privacy'}
                </Link>
                <Link to="/security" className="block text-purple-300 hover:text-white transition-colors">
                  {language === 'fr' ? 'Conditions d\'utilisation' : 'Terms of Service'}
                </Link>
              </div>
            </div>
            
            {/* Contact */}
            <div>
              <h4 className="text-white font-bold mb-4">Contact</h4>
              <div className="space-y-3">
                <Link to="/contact" className="block text-purple-300 hover:text-white transition-colors">
                  {language === 'fr' ? 'Nous contacter' : 'Contact us'}
                </Link>
                <a href="mailto:hello@lifepulse.io" className="block text-purple-300 hover:text-white transition-colors">
                  hello@lifepulse.io
                </a>
              </div>
            </div>
          </div>
          
          {/* Bottom Bar */}
          <div className="border-t border-purple-700/30 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-purple-400 text-sm">
              © {new Date().getFullYear()} LifePulse. {language === 'fr' ? 'Tous droits réservés.' : 'All rights reserved.'}
            </p>
            <div className="flex items-center gap-6">
              <button
                onClick={() => switchLanguage(language === 'fr' ? 'en' : 'fr')}
                className="flex items-center gap-2 text-purple-400 hover:text-white transition-colors text-sm"
              >
                <Globe className="w-4 h-4" />
                {language === 'fr' ? 'English' : 'Français'}
              </button>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};
